/**
 * <SelectionHeaderRow> — la ligne de tête d'une liste à sélection multiple (molecule).
 *
 * Une seule ligne, deux états. Hors sélection, elle montre ce que la page veut dire de sa liste
 * (son titre, son compte) et offre « Sélectionner ». En sélection, elle devient le compte choisi,
 * précédé de la croix qui referme le geste, et offre « Tout sélectionner » / « Tout désélectionner ».
 *
 * La fente gauche est LIBRE hors sélection — « À faire » et sa pastille sur les activités,
 * « 12 activités de côté » sur les archivées — parce que c'est la seule chose qui distingue les
 * deux écrans. Tout le reste est la même ligne, et l'était déjà, recopiée.
 *
 * Entièrement contrôlée : l'état de sélection et le nombre choisi arrivent en props. Un geste sans
 * callback ne s'affiche pas.
 */
import React from "react"
import { Pressable, type StyleProp, type ViewStyle } from "react-native"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"

export type SelectionHeaderRowProps = {
    /** La sélection multiple est ouverte : la ligne passe au compte choisi. */
    selectionMode?: boolean
    /** Le nombre d'éléments choisis — l'accord suit. */
    selectedCount?: number
    /** Tout est choisi : le geste devient « Tout désélectionner ». */
    allSelected?: boolean
    /** Ce que la page dit de sa liste hors sélection — un titre, un compte. */
    children?: React.ReactNode
    /** La croix : referme la sélection. Absent, la croix reste (le compte n'est plus pressable). */
    onCancelSelection?: () => void
    onToggleSelectAll?: () => void
    /** Absent, « Sélectionner » ne s'affiche pas — une liste vide n'a rien à choisir. */
    onStartSelection?: () => void
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function SelectionHeaderRow(props: SelectionHeaderRowProps) {
    const {
        selectionMode = false,
        selectedCount = 0,
        allSelected = false,
        children,
        onCancelSelection,
        onToggleSelectAll,
        onStartSelection,
        style,
        testID,
    } = props

    const plural = selectedCount > 1 ? "s" : ""

    return (
        <Row justify="between" align="center" style={style} testID={testID}>
            {selectionMode ? (
                <Pressable onPress={onCancelSelection} accessibilityRole="button">
                    <Row gap="2" align="center">
                        <Icon name="times" size={16} color="primary" />
                        <Text size="text-md" weight="bold" color="primary">
                            {selectedCount} élément{plural} sélectionné{plural}
                        </Text>
                    </Row>
                </Pressable>
            ) : (
                children
            )}

            {selectionMode
                ? onToggleSelectAll != null && (
                      <TextButton
                          label={allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                          size="sm"
                          onPress={onToggleSelectAll}
                      />
                  )
                : onStartSelection != null && <TextButton label="Sélectionner" size="sm" onPress={onStartSelection} />}
        </Row>
    )
}
